import { useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import { Stack } from '@mui/material';
import { fundOptionsStateJ } from '../../PortfolioOverview/state/FundFilterStateJ';
import { DateRangeSelector } from '../../Finance/form-components/DateRangeSelector';
import { FormatterService } from '../../../util/formatter-service';
import { DateFormattersConfig } from '../../../util/formatters/DateFormatters';
import { defaultShortcuts } from '../../../components/DatePicker/RangeShortcuts';
import { PopupFilter } from '../../../components/PopupFilter';
import { getPlainDateString } from '../../../services/queries/MaggieMetricsQueries';
import { plainDateToZonedDateTime } from '../../../util/date-utilts';
import {
  fundFilterState,
  transactionTypeFilterState,
  transactionsDateFilterState,
} from './AllTransactionsFilterStateJ';
import { useTransactionTypeOptions } from './useTransactionTypeOptions';
import { ExportCashFlowsButton } from './ExportCashFlowsButton';

export function AllTransactionFilters() {
  const [selectedFunds, setSelectedFunds] = useAtom(fundFilterState);
  const fundOptions = useAtomValue(fundOptionsStateJ);
  const transactionTypeOptions = useTransactionTypeOptions();
  const [selectedSubTypes, setSelectedSubTypes] = useAtom(transactionTypeFilterState);

  return (
    <Stack
      display={'grid'}
      gridTemplateColumns='auto auto 1fr auto'
      gap={'1rem'}
      alignItems={'center'}
      justifyContent={'start'}
      width={'100%'}
    >
      <PopupFilter
        selected={selectedFunds}
        setSelected={setSelectedFunds}
        options={fundOptions}
        labels={{ item: 'Fund' }}
        groupable
      />
      <PopupFilter
        groupable
        searchable
        selected={selectedSubTypes}
        setSelected={setSelectedSubTypes}
        options={transactionTypeOptions}
        labels={{ item: 'Transaction Type' }}
      />
      <TransactionDateFilter />
      <ExportCashFlowsButton />
    </Stack>
  );
}

export function TransactionDateFilter() {
  const [selectedDate, setSelectedDate] = useAtom(transactionsDateFilterState);
  const [selectedStartingDate, setSelectedStartingDate] = useState<Date | null>(() =>
    plainDateToZonedDateTime(selectedDate.fromDate)
  );
  const [selectedEndingDate, setSelectedEndingDate] = useState<Date | null>(() =>
    plainDateToZonedDateTime(selectedDate.toDate)
  );

  const dateFormatter = FormatterService.get().getFormatterForModel(DateFormattersConfig.dateNumeric);
  const shortcuts = useMemo(() => defaultShortcuts(), []);

  useEffect(() => {
    if (selectedStartingDate && selectedEndingDate) {
      setSelectedDate({
        fromDate: getPlainDateString(selectedStartingDate),
        toDate: getPlainDateString(selectedEndingDate),
      });
    }
  }, [selectedStartingDate, selectedEndingDate, setSelectedDate]);

  return (
    <DateRangeSelector
      setSelectedStartingDate={setSelectedStartingDate}
      setSelectedEndingDate={setSelectedEndingDate}
      textContent={(selectedStartingDate, selectedEndingDate) => {
        return selectedStartingDate && selectedEndingDate
          ? `${dateFormatter(selectedStartingDate)} - ${dateFormatter(selectedEndingDate)}`
          : 'Select Date Range';
      }}
      style={{ justifySelf: 'end' }}
      initialStartDate={new Date(selectedDate.fromDate)}
      initialEndDate={new Date(selectedDate.toDate)}
      shortcuts={shortcuts}
    />
  );
}
